import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, mergeProps as _mergeProps, withCtx as _withCtx, resolveComponent as _resolveComponent, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, normalizeStyle as _normalizeStyle, createSlots as _createSlots, isRef as _isRef } from "vue"

const _hoisted_1 = { class: "pb-4 print:hidden" }
const _hoisted_2 = {
  key: 1,
  class: "flex justify-end gap-2 pb-2 print:hidden"
}
const _hoisted_3 = { class: "normal-table-container" }
const _hoisted_4 = { class: "text-gray-700 text-sm" }
const _hoisted_5 = { class: "flex items-center gap-1.5" }
const _hoisted_6 = {
  key: 0,
  class: "w-5 h-5 flex items-center justify-center flex-shrink-0"
}
const _hoisted_7 = { class: "text-gray-900 text-sm font-medium" }
const _hoisted_8 = { class: "text-gray-700 text-sm" }
const _hoisted_9 = { class: "text-gray-900 text-sm font-medium" }
const _hoisted_10 = {
  key: 1,
  class: "text-gray-400"
}
const _hoisted_11 = { class: "text-gray-700 text-sm" }
const _hoisted_12 = { class: "text-gray-700 text-sm" }
const _hoisted_13 = { class: "text-gray-700 text-sm" }
const _hoisted_14 = { class: "block truncate max-w-[150px] text-gray-900 text-sm" }
const _hoisted_15 = {
  key: 1,
  class: "text-gray-400 text-sm"
}
const _hoisted_16 = { class: "text-gray-700 text-sm" }
const _hoisted_17 = { class: "p-8" }
const _hoisted_18 = { class: "text-center py-16" }
const _hoisted_19 = { class: "flex mt-4" }
const _hoisted_20 = { class: "pt-1" }
const _hoisted_21 = { class: "block text-xs text-gray-400" }

import { computed, ref, onMounted, onBeforeUnmount, watch } from 'vue';
import type { PropType } from 'vue';
import { storeToRefs } from 'pinia';
import { useMattersStore } from '@/stores/useMattersStore';
import { useMeStore } from '@/services/UserService/useMeStore';
import LlUserAvatar from '@/components/LlUserAvatar.vue';
import LlTooltip from '@/components/LlTooltip.vue';
import LlCheckbox from '@/components/LlCheckbox.vue';
import LlConfirmationDialog from '@/components/LlConfirmationDialog.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlIcon from '@/components/LlIcon.vue';
import LlSelect from '@/components/LlSelect.vue';
import LlDataTable from '@/components/LlDataTable.vue';
import type { SortByItem } from '@/models/llDataTableTypes';
import LlDataTablePagination from '@/components/LlDataTablePagination.vue';
import MattersFilters from './MattersFilters.vue';
import BulkActionBar from './BulkActionBar.vue';
import { useMattersFilters } from './useMattersFilters';
import { formatOrDefault, getShortDateString, getAssignableUsers } from '@/services/utils';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import { useExportUtilities } from '@/composables/useExportUtilities';
import { useNavbarHeight } from '@/composables/useNavbarHeight';
import { useAccountsStore } from '@/services/UserService/useAccountsStore';
import { useRateGroupStore } from '@/services/useRateGroupStore.js';
import { useMattersBulkOperations } from '@/composables/useMattersBulkOperations';
import RouteService from '@/services/RouteService';
import type { UiRouterState } from '@/services/RouteService/UiRouterState';
import splitBillingService from '@/services/splitBillingService';
import axios from 'axios';

// Only the fields this component reads off a matters/table row.
interface MatterRow {
  id: string;
  clientId: string;
  reference: string;
  name: string;
  clientReference: string;
  clientName: string;
  primaryName: string;
  primaryInitials: string;
  opened: string;
  updated: string;
  billingType: string;
  archived: boolean;
  isSecondarySplitMatter: boolean;
  quickBooksId?: string | null;
  goodQBConnection?: boolean;
  rateGroupName?: string | null;
  ledesEnabled?: boolean;
  ledesFormat?: string | null;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'MattersTable',
  props: {
  state: { type: Object as PropType<UiRouterState>, required: false },
},
  setup(__props) {

const props = __props;

const mattersStore = useMattersStore();
const { matters, totalCount, loading } = storeToRefs(mattersStore);

const meStore = useMeStore();
const { me, access, permissions, company, settings, features } = storeToRefs(meStore);

const { handleSuccess, handleError } = useSnackbarHandler();
const { exportCSV, formatDate } = useExportUtilities();
const routeService = new RouteService(props);
const archiveConfirmRef = ref(null);
const exporting = ref(false);
const selectedIds = ref<string[]>([]);
const navbarHeight = useNavbarHeight();

const matterTerm = computed(() => me.value?.terms?.Matter || 'Matter');

// Rate Group / LEDES column visibility - hidden by default (opt-in via the
// Columns dropdown), persisted the same way Time.vue persists its column
// toggles: a debounced PUT /me carrying a settings.matterTableColumns blob,
// rather than a dedicated columns endpoint (Fixed Fees' heavier pattern -
// not warranted for just two fixed toggles).
const showRateGroupColumn = ref(false);
const showLedesColumn = ref(false);
let columnSettingsUpdateTimeout: ReturnType<typeof setTimeout> | null = null;

const initializeColumnVisibilitySettings = () => {
  const matterTableColumns = settings.value?.matterTableColumns || {};
  // Force off regardless of the persisted value if the company no longer has
  // the rateGroups feature (e.g. a downgrade after the column was toggled on) -
  // the Columns dropdown already hides the checkbox for these companies, so a
  // stale true here would otherwise keep requesting a column nothing can show.
  showRateGroupColumn.value = features.value?.rateGroups ? (matterTableColumns.showRateGroup ?? false) : false;
  showLedesColumn.value = matterTableColumns.showLedes ?? false;
};

const persistColumnVisibilitySettings = () => {
  if (columnSettingsUpdateTimeout) {
    clearTimeout(columnSettingsUpdateTimeout);
  }
  columnSettingsUpdateTimeout = setTimeout(async () => {
    await meStore.updateMe({
      settings: {
        matterTableColumns: {
          showRateGroup: showRateGroupColumn.value,
          showLedes: showLedesColumn.value,
        },
      },
    });
    // No try/catch: useMeStore.updateMe() catches its own errors (shows a
    // snackbar) and never rethrows, so a try/catch here would never run.
    // It also only $patches `settings` from the server response on success,
    // never on failure - so settings.value already IS the correct source of
    // truth either way by the time this resolves, and re-reading it here
    // both confirms a success and reverts a failed optimistic update.
    initializeColumnVisibilitySettings();
  }, 300);
};

const toggleRateGroupColumn = (value: boolean) => {
  showRateGroupColumn.value = value;
  // Unlike LEDES (always present in the response, just conditionally
  // displayed), RateGroupName is now only fetched when showRateGroup is
  // true - toggling this column on/off has to reload, or the column would
  // appear with blank data (turning it on) or keep paying for the join one
  // request longer than necessary (turning it off).
  loadMatters();
  persistColumnVisibilitySettings();
};

const toggleLedesColumn = (value: boolean) => {
  showLedesColumn.value = value;
  persistColumnVisibilitySettings();
};

const { filters, queryParams, activeFilterCount, resetFilters } = useMattersFilters();

const handleClearFilters = () => {
  resetFilters();
  loadMatters();
};

// Any change to the filter criteria itself (search, archived, involvement
// scope, QuickBooks check, custom fields) can change which matters are
// in the result set, so a stale selection could silently keep referencing
// matters no longer shown - clear it. Paging/sorting only reorders/pages
// through the SAME result set, so selection is intentionally preserved
// across those (destructure them out rather than listing filter fields
// explicitly, so new filters added later are covered automatically).
const filterSignature = computed(() => {
  const { page, pageSize, orderBy, sortAscending, ...filterFields } = filters.value;
  return JSON.stringify(filterFields);
});

watch(filterSignature, () => {
  selectedIds.value = [];
});

const accountsStore = useAccountsStore();
const { accountsList } = storeToRefs(accountsStore);
const rateGroupStore = useRateGroupStore();
const { rateGroupList } = storeToRefs(rateGroupStore);

const ledesFormats = [
  { id: 'ledes98', label: 'LEDES 1998b' },
  { id: 'ledes2000', label: 'LEDES 2000' },
  { id: 'ledesLss', label: 'LEDES LSS' },
  { id: 'ledesXml2', label: 'LEDES XML 2.0' },
];

const {
  bulkOperationLoading,
  changeResponsibleConfirmRef,
  archiveConfirmRef: bulkArchiveConfirmRef,
  rateGroupConfirmRef,
  ledesEnableConfirmRef,
  ledesDisableConfirmRef,
  selectedAccountId,
  selectedRateGroupId,
  selectedUpdateEntries,
  selectedLedesFormat,
  changeResponsibleConfirmMessage,
  archiveConfirmMessage: bulkArchiveConfirmMessage,
  rateGroupConfirmMessage,
  ledesEnableConfirmMessage,
  ledesDisableConfirmMessage,
  confirmChangeResponsible,
  confirmArchive: confirmBulkArchive,
  confirmApplyRateGroup,
  confirmEnableLedes,
  confirmDisableLedes,
  getMatterProcessState,
  getRowClass,
} = useMattersBulkOperations({
  getMatterIds: () => selectedIds.value,
  clearSelection: () => {
    selectedIds.value = [];
  },
  onAfterOperation: async () => {
    await loadMatters();
  },
  getMatterTerm: () => matterTerm.value,
});

const getRowProps = ({ item }: { item: MatterRow }) => ({
  class: getRowClass(item.id),
});

// Matches matters-next: the QBO status column (icon + header tooltip) only
// exists at all while the connection-check filter is actually applied, not
// just hidden/blank - otherwise there'd be a permanently empty column.
const headers = computed(() => [
  ...(filters.value.checkQuickbooks
    ? [{
      title: '',
      value: 'qbo',
      sortable: false,
      width: 30,
      // Connection status is meaningless on a printed page (no live QBO
      // data), so drop the column entirely rather than print empty cells.
      headerProps: { class: 'print:hidden' },
      cellProps: { class: 'print:hidden' },
    }]
    : []),
  { title: 'Matter ID', value: 'reference', sortable: true, width: 90 },
  { title: 'Matter', value: 'name', sortable: true, width: 220 },
  { title: 'Client ID', value: 'clientReference', sortable: true, width: 90 },
  { title: 'Client', value: 'clientName', sortable: true, width: 200 },
  { title: 'Responsible', value: 'primaryName', sortable: true, width: 160 },
  { title: 'Opened', value: 'opened', sortable: true, width: 100 },
  { title: 'Last Activity', value: 'updated', sortable: true, width: 100 },
  { title: 'Billing', value: 'billingType', sortable: true, width: 100 },
  ...(showRateGroupColumn.value
    ? [{ title: 'Rate Group', value: 'rateGroupName', sortable: true, width: 160 }]
    : []),
  ...(showLedesColumn.value
    ? [{ title: 'LEDES', value: 'ledesFormat', sortable: true, width: 100 }]
    : []),
  { title: '', value: 'actions', sortable: false, width: 50 },
]);

// Sort keys that differ between the v-data-table header value (used for
// cell rendering) and the legacy backend orderBy param name it must send.
const ORDER_BY_MAP: Record<string, string> = {
  reference: 'matterId',
  primaryName: 'primary',
};

const sortBy = computed<SortByItem[]>(() => [
  {
    key: Object.keys(ORDER_BY_MAP).find((k) => ORDER_BY_MAP[k] === filters.value.orderBy) || filters.value.orderBy,
    order: filters.value.sortAscending ? 'asc' : 'desc',
  },
]);

const handleSortChange = (newSort: SortByItem[]) => {
  if (!newSort || newSort.length === 0) return;
  const key = newSort[0].key;
  filters.value.orderBy = ORDER_BY_MAP[key] || key;
  filters.value.sortAscending = newSort[0].order === 'asc';
  filters.value.page = 1;
  loadMatters();
};

const BILLING_TYPE_LABELS: Record<string, string> = {
  hours: 'Hourly',
  none: 'Pro Bono',
  fixed: 'Fixed Fee',
  contingency: 'Contingency',
  internal: 'Internal',
};

const billingTypeDisplay = (billingType: string) => BILLING_TYPE_LABELS[billingType] || '';

// Matches ClientLedes.vue's `formats` list (id -> full label) - the table
// column shows a short form instead of the full label to keep the column narrow.
const LEDES_FORMAT_LABELS: Record<string, string> = {
  ledes98: '98B',
  ledes2000: '2000',
  ledesLss: 'LSS',
  ledesXml2: 'XML',
};

const ledesFormatLabel = (format: string | null | undefined) => (format ? LEDES_FORMAT_LABELS[format] || format : '');

const loadMatters = () => {
  // knownCount lets the backend skip re-running COUNT(*) on pages after the
  // first (matches legacy buildQuery()'s same optimization).
  const params = { ...queryParams.value };
  if (filters.value.page > 1) {
    params.knownCount = totalCount.value;
  }
  // Lets the backend skip the RateGroups join/column entirely when this page
  // isn't displaying it - both when the company lacks the feature (the
  // Columns dropdown already keeps this false in that case, see
  // initializeColumnVisibilitySettings) and when a user with the feature
  // simply hasn't toggled the column on.
  params.showRateGroup = showRateGroupColumn.value;
  return mattersStore.listMatters(params);
};

const handlePageChange = (page: number) => {
  filters.value.page = page;
  loadMatters();
};

const handlePageSizeChange = (pageSize: number) => {
  filters.value.pageSize = pageSize;
  filters.value.page = 1;
  loadMatters();
};

const handleCreateMatter = () => {
  // Matches legacy redirectToCreate(): accounts on the new time-entry
  // experience get the Vue create-matter modal, everyone else the legacy
  // form. Both are child states of `matters` itself (an `edit` named view
  // layered on top of this page) so the URL stays under /matters instead of
  // navigating to matters-next.
  if (features.value?.timeEntriesTab && settings.value?.enableNewTimeEntry) {
    routeService.go('matters.matter2', { methodOverride: 'matter page' });
    return;
  }
  routeService.go('matters.newMatter', { methodOverride: 'matter page' });
};

const handlePrint = () => {
  window.print();
};

const handleConflictCheck = () => {
  routeService.go('conflict');
};

// Regression to matters-next's old behavior: the per-matter QuickBooks
// connection check is opt-in and explicitly triggered from this button (not
// automatically run whenever the QB Connection dropdown - hidden until this
// has run at least once - is touched). See MattersFilters.vue's checkQuickbooks
// gate on that dropdown.
const handleValidateQuickbooks = () => {
  filters.value.checkQuickbooks = true;
  filters.value.page = 1;
  loadMatters();
};

const handleExport = async () => {
  exporting.value = true;
  try {
    const response = await axios({
      method: 'GET',
      url: '/matters/table',
      params: {
        ...queryParams.value,
        limit: totalCount.value,
        offset: 0,
        showRateGroup: showRateGroupColumn.value,
      },
    });
    await exportCSV(
      response.data.items || [],
      {
        'Matter Id': 'reference',
        Matter: 'name',
        'Client Id': 'clientReference',
        Client: 'clientName',
        Responsible: 'primaryName',
        'Last Activity': (item: MatterRow) => formatDate(item.updated),
        Opened: (item: MatterRow) => formatDate(item.opened),
        Billing: (item: MatterRow) => billingTypeDisplay(item.billingType),
        // Only exported when the user has actually opted into seeing them via
        // the Columns dropdown - matches what's currently shown in the table.
        ...(showRateGroupColumn.value
          ? { 'Rate Group': (item: MatterRow) => item.rateGroupName || 'Custom rates' }
          : {}),
        ...(showLedesColumn.value
          ? { LEDES: (item: MatterRow) => (item.ledesEnabled ? ledesFormatLabel(item.ledesFormat) : '-') }
          : {}),
      },
      { filename: 'Matters' },
    );
  } catch (error) {
    handleError(error);
  } finally {
    exporting.value = false;
  }
};

const viewMatter = (item: MatterRow) => {
  // Matches matters-next's showMatter(matter, true): $state.go('.client.matter', ...)
  // redirects to .client.matter.info (the Info tab), not Overview.
  routeService.go('matters-next.client.matter.info', {
    id: item.clientId,
    matterId: item.id,
  });
};

// Matches matters-next's row behavior: every cell navigates to the matter
// except genuinely interactive elements (the kebab menu button now, the
// selection checkboxes once bulk actions land).
const handleRowClick = (event: MouseEvent, { item }: { item: MatterRow }) => {
  const target = event.target as HTMLElement;
  const isActionClick =
    target.closest('button') ||
    target.closest('.v-selection-control') ||
    target.closest('a') ||
    target.closest('.v-checkbox') ||
    target.closest('.v-data-table__td--select');

  if (isActionClick) return;

  viewMatter(item);
};

const viewClient = (item: MatterRow) => {
  routeService.go('clients.client.view.overview', { id: item.clientId });
};

const openQuickbooksConnector = (item: MatterRow) => {
  routeService.go('matters.connect', { matterId: item.id });
};

const openTrustSetup = (item: MatterRow) => {
  routeService.go('matters.trust', { matterId: item.id });
};

const archiveConfirmMessage = computed(
  () =>
    `Archiving the ${matterTerm.value} will hide it from all users and hide any unbilled time or expenses from some pages. You can easily restore the ${matterTerm.value} from here if you need it again.`,
);

const handleArchiveToggle = async (item: MatterRow, activate: boolean) => {
  if (item.isSecondarySplitMatter) {
    await splitBillingService.showActionRestrictionIfNeeded(
      item.id,
      matterTerm.value,
      activate ? 'Restore' : 'Archive',
    );
    return;
  }

  if (!activate) {
    const confirmed = await archiveConfirmRef.value?.open();
    if (!confirmed) return;
  }

  try {
    await axios.put(`/matters/${item.id}`, { active: activate });
    handleSuccess(
      activate
        ? `The ${matterTerm.value} was restored.`
        : `The ${matterTerm.value} was archived. You can find it on the lists by selecting 'Show Archived'.`,
    );
    await loadMatters();
  } catch (error) {
    handleError(error);
  }
};

onMounted(async () => {
  // Must run before loadMatters() - showRateGroupColumn now determines the
  // showRateGroup request param, so initializing it after the first load
  // would send that request with the wrong value (always false) and leave
  // the Rate Group column visible but blank until a later reload.
  initializeColumnVisibilitySettings();
  loadMatters();
  if (!accountsList.value?.length) {
    await accountsStore.listAccounts();
  }
  if (!rateGroupList.value?.length) {
    await rateGroupStore.listRateGroup();
  }
});

onBeforeUnmount(() => {
  if (columnSettingsUpdateTimeout) {
    clearTimeout(columnSettingsUpdateTimeout);
  }
});

return (_ctx: any,_cache: any) => {
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_list = _resolveComponent("v-list")!
  const _component_v_menu = _resolveComponent("v-menu")!
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_list_item_title = _resolveComponent("v-list-item-title")!
  const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(MattersFilters, {
        filters: _unref(filters),
        loading: _unref(loading),
        "active-filter-count": _unref(activeFilterCount),
        onApplyFilters: loadMatters,
        onClearFilters: handleClearFilters
      }, null, 8, ["filters", "loading", "active-filter-count"])
    ]),
    _createElementVNode("div", {
      class: "matters-toolbar-sticky",
      style: _normalizeStyle({ top: `${_unref(navbarHeight)}px` })
    }, [
      (selectedIds.value.length && _unref(permissions)?.matters?.create)
        ? (_openBlock(), _createBlock(BulkActionBar, {
            key: 0,
            "selected-ids": selectedIds.value,
            archived: _unref(filters).archived,
            "matter-term": matterTerm.value,
            "bulk-operation-loading": _unref(bulkOperationLoading),
            "can-change-responsible": !!_unref(permissions)?.matters?.assign,
            "can-apply-rate-group": !!_unref(permissions)?.matters?.rates && !!_unref(permissions)?.matters?.assign,
            "can-archive": !!_unref(access)?.billing,
            onClear: _cache[0] || (_cache[0] = ($event: any) => (selectedIds.value = [])),
            onChangeResponsible: _unref(confirmChangeResponsible),
            onArchive: _cache[1] || (_cache[1] = ($event: any) => (_unref(confirmBulkArchive)(!_unref(filters).archived))),
            onApplyRateGroup: _unref(confirmApplyRateGroup),
            onEnableLedes: _unref(confirmEnableLedes),
            onDisableLedes: _unref(confirmDisableLedes)
          }, null, 8, ["selected-ids", "archived", "matter-term", "bulk-operation-loading", "can-change-responsible", "can-apply-rate-group", "can-archive", "onChangeResponsible", "onApplyRateGroup", "onEnableLedes", "onDisableLedes"]))
        : (_openBlock(), _createElementBlock("div", _hoisted_2, [
            _createVNode(_component_v_menu, { "close-on-content-click": false }, {
              activator: _withCtx(({ props: menuProps }) => [
                _createVNode(LlSecondaryButton, _mergeProps(menuProps, {
                  variant: "outlined",
                  "append-icon": "mdi-menu-down",
                  "data-cy": "matters-columns-menu-button"
                }), {
                  default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                    _createTextVNode(" Columns ", -1)
                  ]))]),
                  _: 1
                }, 16)
              ]),
              default: _withCtx(() => [
                _createVNode(_component_v_list, {
                  density: "compact",
                  class: "py-0"
                }, {
                  default: _withCtx(() => [
                    (_unref(features)?.rateGroups)
                      ? (_openBlock(), _createBlock(_component_v_list_item, { key: 0 }, {
                          default: _withCtx(() => [
                            _createVNode(LlCheckbox, {
                              "model-value": showRateGroupColumn.value,
                              "onUpdate:modelValue": toggleRateGroupColumn,
                              label: "Rate Group",
                              "label-class": "text-sm",
                              density: "compact",
                              "data-cy": "matters-column-rate-group"
                            }, null, 8, ["model-value"])
                          ]),
                          _: 1
                        }))
                      : _createCommentVNode("", true),
                    _createVNode(_component_v_list_item, null, {
                      default: _withCtx(() => [
                        _createVNode(LlCheckbox, {
                          "model-value": showLedesColumn.value,
                          "onUpdate:modelValue": toggleLedesColumn,
                          label: "LEDES",
                          "label-class": "text-sm",
                          density: "compact",
                          "data-cy": "matters-column-ledes"
                        }, null, 8, ["model-value"])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(LlSecondaryButton, {
              variant: "outlined",
              "append-icon": "mdi-shield-search",
              onClick: handleConflictCheck
            }, {
              default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                _createTextVNode(" Conflict Check ", -1)
              ]))]),
              _: 1
            }),
            _createVNode(LlSecondaryButton, {
              variant: "outlined",
              "append-icon": "mdi-printer",
              onClick: handlePrint
            }, {
              default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                _createTextVNode(" Print ", -1)
              ]))]),
              _: 1
            }),
            _createVNode(LlSecondaryButton, {
              variant: "outlined",
              "append-icon": "mdi-download",
              disabled: exporting.value,
              onClick: handleExport
            }, {
              default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                _createTextVNode(" Export ", -1)
              ]))]),
              _: 1
            }, 8, ["disabled"]),
            (_unref(company)?.integration?.quickbooks && _unref(access)?.billing)
              ? (_openBlock(), _createBlock(LlSecondaryButton, {
                  key: 0,
                  variant: "outlined",
                  "append-icon": "mdi-cloud-sync-outline",
                  loading: _unref(loading),
                  disabled: _unref(loading),
                  onClick: handleValidateQuickbooks
                }, {
                  default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                    _createTextVNode(" Validate QuickBooks Connection ", -1)
                  ]))]),
                  _: 1
                }, 8, ["loading", "disabled"]))
              : _createCommentVNode("", true),
            _createVNode(LlSecondaryButton, {
              color: "primary",
              variant: "flat",
              onClick: handleCreateMatter
            }, {
              default: _withCtx(() => [
                _createVNode(LlIcon, { class: "mr-1" }, {
                  default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                    _createTextVNode("mdi-plus", -1)
                  ]))]),
                  _: 1
                }),
                _createTextVNode(" Create " + _toDisplayString(matterTerm.value), 1)
              ]),
              _: 1
            })
          ]))
    ], 4),
    _createElementVNode("div", _hoisted_3, [
      _createVNode(LlDataTable, {
        headers: headers.value,
        items: _unref(matters),
        "items-length": _unref(totalCount),
        loading: _unref(loading),
        "sort-by": sortBy.value,
        "item-value": "id",
        class: "matters-table",
        "show-select": "",
        "model-value": selectedIds.value,
        "row-props": getRowProps,
        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (selectedIds.value = $event)),
        "onUpdate:sortBy": handleSortChange,
        "onClick:row": handleRowClick
      }, _createSlots({
        "header.qbo": _withCtx(() => [
          (_unref(company)?.integration?.quickbooks && _unref(access)?.billing)
            ? (_openBlock(), _createBlock(LlTooltip, {
                key: 0,
                text: "A green icon in this column shows that matter is connected to QuickBooks and invoices can be generated.",
                icon: "help"
              }))
            : _createCommentVNode("", true)
        ]),
        "item.qbo": _withCtx(({ item }) => [
          (_unref(filters).checkQuickbooks && item.quickBooksId)
            ? (_openBlock(), _createBlock(LlIcon, {
                key: 0,
                icon: item.goodQBConnection ? 'mdi-circle' : 'mdi-circle',
                color: item.goodQBConnection ? 'success' : 'error',
                size: "10"
              }, null, 8, ["icon", "color"]))
            : _createCommentVNode("", true)
        ]),
        "item.reference": _withCtx(({ item }) => [
          _createElementVNode("span", _hoisted_4, _toDisplayString(_unref(formatOrDefault)(item.reference)), 1)
        ]),
        "item.name": _withCtx(({ item }) => [
          _createElementVNode("div", _hoisted_5, [
            (_unref(getMatterProcessState)(item.id).processing || _unref(getMatterProcessState)(item.id).error)
              ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                  (_unref(getMatterProcessState)(item.id).processing)
                    ? (_openBlock(), _createBlock(_component_v_progress_circular, {
                        key: 0,
                        indeterminate: "",
                        size: "16",
                        width: "2",
                        color: "primary"
                      }))
                    : (_openBlock(), _createBlock(LlTooltip, {
                        key: 1,
                        text: _unref(getMatterProcessState)(item.id).error,
                        location: "top"
                      }, {
                        activator: _withCtx(() => [
                          _createVNode(LlIcon, {
                            icon: "mdi-alert",
                            size: "18",
                            color: "warning",
                            class: "cursor-help"
                          })
                        ]),
                        _: 1
                      }, 8, ["text"]))
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("span", _hoisted_7, _toDisplayString(_unref(formatOrDefault)(item.name)), 1)
          ])
        ]),
        "item.clientReference": _withCtx(({ item }) => [
          _createElementVNode("span", _hoisted_8, _toDisplayString(_unref(formatOrDefault)(item.clientReference)), 1)
        ]),
        "item.clientName": _withCtx(({ item }) => [
          _createElementVNode("span", _hoisted_9, _toDisplayString(_unref(formatOrDefault)(item.clientName)), 1)
        ]),
        "item.primaryName": _withCtx(({ item }) => [
          (item.primaryName)
            ? (_openBlock(), _createBlock(LlUserAvatar, {
                key: 0,
                initials: item.primaryInitials,
                name: item.primaryName
              }, null, 8, ["initials", "name"]))
            : (_openBlock(), _createElementBlock("span", _hoisted_10, "—"))
        ]),
        "item.opened": _withCtx(({ item }) => [
          _createElementVNode("span", _hoisted_11, _toDisplayString(_unref(getShortDateString)(item.opened)), 1)
        ]),
        "item.updated": _withCtx(({ item }) => [
          _createElementVNode("span", _hoisted_12, _toDisplayString(_unref(getShortDateString)(item.updated)), 1)
        ]),
        "item.billingType": _withCtx(({ item }) => [
          _createElementVNode("span", _hoisted_13, _toDisplayString(billingTypeDisplay(item.billingType)), 1)
        ]),
        "item.actions": _withCtx(({ item }) => [
          _createVNode(_component_v_menu, null, {
            activator: _withCtx(({ props: menuProps }) => [
              _createVNode(LlSecondaryButton, _mergeProps({
                icon: "mdi-dots-vertical",
                variant: "text",
                size: "small",
                density: "comfortable",
                "aria-label": `${matterTerm.value} actions`
              }, menuProps), null, 16, ["aria-label"])
            ]),
            default: _withCtx(() => [
              _createVNode(_component_v_list, { density: "compact" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_list_item, {
                    onClick: ($event: any) => (viewMatter(item))
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_list_item_title, null, {
                        default: _withCtx(() => [
                          _createTextVNode("View " + _toDisplayString(matterTerm.value), 1)
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }, 8, ["onClick"]),
                  _createVNode(_component_v_list_item, {
                    onClick: ($event: any) => (viewClient(item))
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_list_item_title, null, {
                        default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                          _createTextVNode("View Client", -1)
                        ]))]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }, 8, ["onClick"]),
                  (_unref(company)?.integration?.quickbooks && _unref(access)?.billing)
                    ? (_openBlock(), _createBlock(_component_v_list_item, {
                        key: 0,
                        onClick: ($event: any) => (openQuickbooksConnector(item))
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_list_item_title, null, {
                            default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                              _createTextVNode("QuickBooks Connector", -1)
                            ]))]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }, 8, ["onClick"]))
                    : _createCommentVNode("", true),
                  (item.quickBooksId)
                    ? (_openBlock(), _createBlock(_component_v_list_item, {
                        key: 1,
                        onClick: ($event: any) => (openTrustSetup(item))
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_list_item_title, null, {
                            default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                              _createTextVNode("Trust Account Setup", -1)
                            ]))]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }, 8, ["onClick"]))
                    : _createCommentVNode("", true),
                  (_unref(access)?.billing && !item.archived)
                    ? (_openBlock(), _createBlock(_component_v_list_item, {
                        key: 2,
                        onClick: ($event: any) => (handleArchiveToggle(item, false))
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_list_item_title, null, {
                            default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                              _createTextVNode("Archive", -1)
                            ]))]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }, 8, ["onClick"]))
                    : _createCommentVNode("", true),
                  (_unref(access)?.billing && item.archived)
                    ? (_openBlock(), _createBlock(_component_v_list_item, {
                        key: 3,
                        onClick: ($event: any) => (handleArchiveToggle(item, true))
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_list_item_title, null, {
                            default: _withCtx(() => [...(_cache[17] || (_cache[17] = [
                              _createTextVNode("Activate", -1)
                            ]))]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }, 8, ["onClick"]))
                    : _createCommentVNode("", true)
                ]),
                _: 2
              }, 1024)
            ]),
            _: 2
          }, 1024)
        ]),
        loading: _withCtx(() => [
          _createElementVNode("div", _hoisted_17, [
            _createVNode(_component_v_skeleton_loader, {
              type: "table-row@10",
              class: "w-full"
            })
          ])
        ]),
        "no-data": _withCtx(() => [
          _createElementVNode("div", _hoisted_18, [
            _createVNode(LlIcon, {
              icon: "mdi-briefcase-outline",
              size: "64",
              color: "grey-lighten-2",
              class: "mb-4"
            }),
            _cache[18] || (_cache[18] = _createElementVNode("p", { class: "text-xl font-medium text-gray-600 mb-2" }, "No matters found", -1))
          ])
        ]),
        _: 2
      }, [
        (showRateGroupColumn.value)
          ? {
              name: "item.rateGroupName",
              fn: _withCtx(({ item }) => [
                (item.rateGroupName)
                  ? (_openBlock(), _createBlock(LlTooltip, {
                      key: 0,
                      text: item.rateGroupName,
                      location: "top"
                    }, {
                      activator: _withCtx(() => [
                        _createElementVNode("span", _hoisted_14, _toDisplayString(item.rateGroupName), 1)
                      ]),
                      _: 2
                    }, 1032, ["text"]))
                  : (_openBlock(), _createElementBlock("span", _hoisted_15, "Custom rates"))
              ]),
              key: "0"
            }
          : undefined,
        (showLedesColumn.value)
          ? {
              name: "item.ledesFormat",
              fn: _withCtx(({ item }) => [
                _createElementVNode("span", _hoisted_16, _toDisplayString(item.ledesEnabled ? ledesFormatLabel(item.ledesFormat) : '-'), 1)
              ]),
              key: "1"
            }
          : undefined
      ]), 1032, ["headers", "items", "items-length", "loading", "sort-by", "model-value"])
    ]),
    _createVNode(LlDataTablePagination, {
      sticky: "",
      class: "print:hidden",
      page: _unref(filters).page,
      "page-size": _unref(filters).pageSize,
      "total-count": _unref(totalCount),
      "onUpdate:page": handlePageChange,
      "onUpdate:pageSize": handlePageSizeChange
    }, null, 8, ["page", "page-size", "total-count"]),
    _createVNode(LlConfirmationDialog, {
      ref_key: "archiveConfirmRef",
      ref: archiveConfirmRef,
      title: `Archive this ${matterTerm.value}?`,
      message: archiveConfirmMessage.value,
      "toggle-text": "Cancel",
      "confirm-text": "Archive",
      "show-warning-icon": false,
      "preserve-newlines": "",
      "confirm-color": "primary",
      "confirm-variant": "flat"
    }, null, 8, ["title", "message"]),
    _createVNode(LlConfirmationDialog, {
      ref_key: "changeResponsibleConfirmRef",
      ref: changeResponsibleConfirmRef,
      title: "Change Responsible",
      message: _unref(changeResponsibleConfirmMessage),
      "confirm-text": "Change Responsible",
      "show-warning-icon": false,
      "confirm-disabled": !_unref(selectedAccountId),
      "confirm-color": "primary",
      "confirm-variant": "flat"
    }, {
      content: _withCtx(() => [
        _createVNode(LlSelect, {
          modelValue: _unref(selectedAccountId),
          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => (_isRef(selectedAccountId) ? (selectedAccountId).value = $event : null)),
          items: _unref(getAssignableUsers)(_unref(accountsList) || []),
          "item-title": "name",
          "item-value": "id",
          "return-object": false,
          label: "Responsible attorney",
          placeholder: "Select an attorney",
          density: "compact",
          "hide-details": ""
        }, null, 8, ["modelValue", "items"])
      ]),
      _: 1
    }, 8, ["message", "confirm-disabled"]),
    _createVNode(LlConfirmationDialog, {
      ref_key: "bulkArchiveConfirmRef",
      ref: bulkArchiveConfirmRef,
      title: `${_unref(filters).archived ? 'Restore' : 'Archive'} ${matterTerm.value}s?`,
      message: _unref(bulkArchiveConfirmMessage)(!_unref(filters).archived),
      "confirm-text": _unref(filters).archived ? 'Restore' : 'Archive',
      "show-warning-icon": false,
      "preserve-newlines": "",
      "confirm-color": "primary",
      "confirm-variant": "flat"
    }, null, 8, ["title", "message", "confirm-text"]),
    _createVNode(LlConfirmationDialog, {
      ref_key: "rateGroupConfirmRef",
      ref: rateGroupConfirmRef,
      title: "Apply Rate Group",
      message: _unref(rateGroupConfirmMessage),
      "confirm-text": "Apply",
      "show-warning-icon": false,
      "confirm-disabled": !_unref(selectedRateGroupId),
      "confirm-color": "primary",
      "confirm-variant": "flat"
    }, {
      content: _withCtx(() => [
        _createVNode(LlSelect, {
          modelValue: _unref(selectedRateGroupId),
          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => (_isRef(selectedRateGroupId) ? (selectedRateGroupId).value = $event : null)),
          items: _unref(rateGroupList) || [],
          "item-title": "name",
          "item-value": "rateGroupId",
          "return-object": false,
          label: "Rate group",
          placeholder: "Select a rate group",
          density: "compact",
          "hide-details": ""
        }, null, 8, ["modelValue", "items"]),
        _createElementVNode("div", _hoisted_19, [
          _createVNode(LlCheckbox, {
            modelValue: _unref(selectedUpdateEntries),
            "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => (_isRef(selectedUpdateEntries) ? (selectedUpdateEntries).value = $event : null)),
            "aria-label": "Update time entries"
          }, null, 8, ["modelValue"]),
          _createElementVNode("div", _hoisted_20, [
            _cache[19] || (_cache[19] = _createElementVNode("span", { class: "text-sm" }, "Update time entries", -1)),
            _createElementVNode("span", _hoisted_21, " All WIP time entries for the affected " + _toDisplayString(matterTerm.value) + "(s) will be updated to use the rate group's rates. ", 1)
          ])
        ])
      ]),
      _: 1
    }, 8, ["message", "confirm-disabled"]),
    _createVNode(LlConfirmationDialog, {
      ref_key: "ledesEnableConfirmRef",
      ref: ledesEnableConfirmRef,
      title: "Enable LEDES",
      message: `${_unref(ledesEnableConfirmMessage)}\n\nLEDES is configured per-client, so this will apply to every matter sharing a client with your selection - not just the matters selected.`,
      "confirm-text": "Enable",
      "show-warning-icon": false,
      "preserve-newlines": "",
      "confirm-disabled": !_unref(selectedLedesFormat),
      "confirm-color": "primary",
      "confirm-variant": "flat"
    }, {
      content: _withCtx(() => [
        _createVNode(LlSelect, {
          modelValue: _unref(selectedLedesFormat),
          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => (_isRef(selectedLedesFormat) ? (selectedLedesFormat).value = $event : null)),
          items: ledesFormats,
          "item-title": "label",
          "item-value": "id",
          "return-object": false,
          label: "LEDES format",
          placeholder: "Select a format",
          density: "compact",
          "hide-details": ""
        }, null, 8, ["modelValue"])
      ]),
      _: 1
    }, 8, ["message", "confirm-disabled"]),
    _createVNode(LlConfirmationDialog, {
      ref_key: "ledesDisableConfirmRef",
      ref: ledesDisableConfirmRef,
      title: "Disable LEDES",
      message: `${_unref(ledesDisableConfirmMessage)}\n\nLEDES is configured per-client, so this will apply to every matter sharing a client with your selection - not just the matters selected.`,
      "confirm-text": "Disable",
      "show-warning-icon": false,
      "preserve-newlines": "",
      "confirm-color": "primary",
      "confirm-variant": "flat"
    }, null, 8, ["message"])
  ]))
}
}

})